import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog, SelectChangeEvent, TextField } from '@mui/material';
import { RegistryWidgetsType } from '@rjsf/utils';
import { TextSpan, TextBlock } from '../../Atoms/Text';
import { Account, InventoryAsset, Product } from '../../../types';
import { NewLocationEntry } from '../../../Api/ServiceRequest/serviceRequestApi';
import {
  CheckboxInput,
  CompoundTextInput,
  CurrencyInput,
  CustomFieldTemplate,
  DateInput,
  IntegerInput,
  JSONSchemaForm,
  MultiSelectInput,
  CustomObjectFieldTemplate,
  ProductAddressInput,
  RadioNumberInput,
  SelectInput,
  ShowAdvancedButton,
  StatusSelectionInput,
  SupplierSelectionInput,
  TermSelectionInput,
  TextInput
} from '../JSONSchemaForm';
import { AccountSelector } from './AccountSelector';
import { AttachmentsSection } from './AttachmentsSection';
import { buildServiceRequest } from './buildServiceRequest';
import { AssetSelectionField } from './AssetSelectionField';
import { DisconnectFields } from './DisconnectFields';
import { LocationFields } from './LocationFields';
import { PreferredTermField } from './PreferredTermField';
import { isRequestFormValid } from './requestFormValidity';
import { ProductField } from './ProductField';
import { RequestTypeField } from './RequestTypeField';
import { RequestFormActions } from './RequestFormActions';
import { RequestTabs } from './RequestTabs';
import {
  MAX_REQUESTS,
  MOVE_REQUEST,
  NEW_SERVICE_REQUEST,
  ServiceRequestFormValues,
  LocationScope,
  allowedProductsOf,
  managedProductsOf,
  RequestSnapshot,
  usesAccountProducts,
  CHANGE_REQUEST,
  DISCONNECT_REQUEST,
  isPointToPoint,
  requiresAsset
} from './serviceRequestModel';
import { useAccountSelection } from './useAccountSelection';
import { useLocationOptions } from './useLocationOptions';
import { useLocationScope } from './useLocationScope';
import { useOrderFormData } from './useOrderFormData';
import { useRequestSubmit } from './useRequestSubmit';
import { useRequestTabs } from './useRequestTabs';
import { useSchemaForm } from './useSchemaForm';
import { useStagedAttachments } from './useStagedAttachments';

const widgets: RegistryWidgetsType = {
  CheckboxInput,
  SupplierSelectionInput,
  TermSelectionInput,
  StatusSelectionInput,
  ProductAddressInput,
  TextInput,
  DateInput,
  CurrencyInput,
  ShowAdvancedButton,
  IntegerInput,
  SelectInput,
  MultiSelectInput,
  RadioNumberInput,
  CompoundTextInput
};

export interface ServiceRequestFormProps {
  open: boolean;
  onClose: () => void;
  location?: LocationScope;
}

/** The New Request dialog: one or more requests filed under one account, started at a location when opened from its drawer. */
export function ServiceRequestForm({ open, onClose, location }: ServiceRequestFormProps) {
  const [requestType, setRequestType] = useState<string>(NEW_SERVICE_REQUEST);
  const [productSelection, setProductSelection] = useState<Product | null>(null);
  const [newLocations, setNewLocations] = useState<NewLocationEntry[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<InventoryAsset | null>(null);
  const [selectedDisconnectInventories, setSelectedDisconnectInventories] = useState<InventoryAsset[]>([]);
  const [locationA, setLocationA] = useState<string | null>(null);
  const [locationZ, setLocationZ] = useState<string | null>(null);
  const [preferredTermSelections, setPreferredTermSelections] = useState<string[]>([]);
  const {
    resetToDefault,
    selectedAccount,
    setSelectedAccount,
    showAccountSelector,
    accountSelectionMissing,
    accountOptions
  } = useAccountSelection(location);

  const { register, handleSubmit, reset, watch, getValues } = useForm<ServiceRequestFormValues>({
    defaultValues: {
      request_type: NEW_SERVICE_REQUEST,
      service_types: [],
      details: ''
    }
  });
  const { allProducts, existingInventory, orderRequestSchemas, orderRequestPrefs } = useOrderFormData();
  const displayLocationCode = orderRequestPrefs.displayLocationCode ?? false;
  const {
    showAdvancedLayout,
    orderRequestSchema,
    requestSchema,
    dynamicUiSchema,
    schemaFormData,
    setSchemaFormData,
    schemaFormErrors,
    setSchemaFormErrors,
    handleSchemaFormChange,
    resetSchemaForm
  } = useSchemaForm({ requestType, productSelection, orderRequestSchemas, orderRequestPrefs });
  // The snapshot functions read state declared below, so the tabs hook gets them as closures, not values.
  const {
    requests,
    setRequests,
    activeTab,
    anotherRequest,
    setAnotherRequest,
    revealTab,
    handleContinue,
    handleTabChange,
    handleRemoveTab,
    resetTabs
  } = useRequestTabs({
    captureSnapshot: () => captureSnapshot(),
    restoreSnapshot: (snapshot) => restoreSnapshot(snapshot)
  });
  const {
    stagedAttachments,
    setStagedAttachments,
    attachmentsSupported,
    updateStagedAttachment,
    ...attachmentActions
  } = useStagedAttachments(requestType, requests, activeTab);

  const isP2PProduct = useMemo(() => isPointToPoint(productSelection), [productSelection]);
  const showAssetSelection = useMemo(
    () => requiresAsset(requestType, productSelection),
    [requestType, productSelection]
  );

  useEffect(() => {
    if (!showAssetSelection) setSelectedAsset(null);
  }, [showAssetSelection]);

  const { scopedInventory, installedHere, locationInventory } = useLocationScope({
    location,
    selectedAccount,
    accountSelectorShown: showAccountSelector,
    existingInventory
  });
  const { filteredLocations, locationSelections, setLocationSelections, seedFromLocation, cancelSeed } =
    useLocationOptions({
      location,
      requestType,
      productSelection,
      scopedInventory,
      displayLocationCode
    });

  // Declared after useLocationOptions on purpose: its filter effect must run first, or the seed is pruned.
  useEffect(() => {
    if (open) seedFromLocation(resetToDefault());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const accountProducts = useMemo(() => managedProductsOf(locationInventory), [locationInventory]);
  const allowedProducts = useMemo(
    () => allowedProductsOf(allProducts, orderRequestPrefs),
    [allProducts, orderRequestPrefs]
  );
  const productOptions = usesAccountProducts(requestType) ? accountProducts : allowedProducts;

  const handleAccountChange = (_: React.SyntheticEvent<Element, Event>, value: Account | null) => {
    setSelectedAccount(value);
    // Inventory-derived picks belong to the old account; global-catalog products and free-text locations stay.
    if (usesAccountProducts(requestType)) setProductSelection(null);
    setLocationSelections([]);
    setLocationA(null);
    setLocationZ(null);
    setSelectedAsset(null);
    setSelectedDisconnectInventories([]);
    setRequests((prev) =>
      prev.map((request) =>
        request.status === 'success'
          ? request
          : {
              ...request,
              productSelection: usesAccountProducts(request.request_type) ? null : request.productSelection,
              locationSelections: [],
              locationA: null,
              locationZ: null,
              selectedAsset: null,
              selectedDisconnectInventories: []
            }
      )
    );
  };

  const resetFormData = () => {
    resetSubmit();
    setRequestType(NEW_SERVICE_REQUEST);
    setProductSelection(null);
    setLocationSelections([]);
    setNewLocations([]);
    resetSchemaForm();
    setSelectedAsset(null);
    setSelectedDisconnectInventories([]);
    setLocationA(null);
    setLocationZ(null);
    setPreferredTermSelections([]);
    setStagedAttachments([]);
    cancelSeed();
    resetToDefault();
    reset();
    resetTabs();
  };

  const handleClose = () => {
    resetFormData();
    onClose();
  };

  const captureSnapshot = (): RequestSnapshot => ({
    request_type: requestType,
    preferred_term: preferredTermSelections,
    details: getValues('details'),
    productSelection,
    locationSelections,
    newLocations,
    selectedAsset,
    selectedDisconnectInventories,
    locationA,
    locationZ,
    schemaFormData,
    schemaFormErrors,
    attachments: stagedAttachments,
    opportunityId: requests[activeTab]?.opportunityId ?? null,
    status: requests[activeTab]?.status ?? 'pending'
  });

  const restoreSnapshot = (snapshot: RequestSnapshot) => {
    setRequestType(snapshot.request_type);
    setProductSelection(snapshot.productSelection);
    setLocationSelections(snapshot.locationSelections);
    setNewLocations(snapshot.newLocations);
    setSelectedAsset(snapshot.selectedAsset);
    setSelectedDisconnectInventories(snapshot.selectedDisconnectInventories);
    setLocationA(snapshot.locationA);
    setLocationZ(snapshot.locationZ);
    setPreferredTermSelections(snapshot.preferred_term);
    setSchemaFormData(snapshot.schemaFormData);
    setSchemaFormErrors(snapshot.schemaFormErrors);
    setStagedAttachments(snapshot.attachments);
    reset({
      request_type: snapshot.request_type,
      details: snapshot.details,
      service_types: []
    });
  };

  const { disabled, resetSubmit, handleSubmitAll } = useRequestSubmit({
    requests,
    activeTab,
    captureSnapshot,
    revealTab,
    buildRequest: (snapshot) => buildServiceRequest(snapshot, { displayLocationCode, scopedInventory, installedHere }),
    accountId: showAccountSelector || location ? selectedAccount?.id : undefined,
    accountSelectionMissing,
    onRequestsChange: setRequests,
    onUploadProgress: updateStagedAttachment,
    onSent: () => {
      resetFormData();
      onClose();
    }
  });

  const productSelectionChanged = (e: React.SyntheticEvent<Element, Event>, value: Product | null) => {
    setProductSelection(value);
    if (requestType === DISCONNECT_REQUEST) {
      setSelectedDisconnectInventories([]);
    }
    if (requestType === MOVE_REQUEST) {
      setSelectedAsset(null);
    }
  };

  const requestTypeChanged = (event: SelectChangeEvent) => {
    setRequestType(event.target.value);
    setProductSelection(null);
    if (location && event.target.value === MOVE_REQUEST) setLocationSelections([]);
  };

  const filteredInventory = useMemo(() => {
    return locationInventory.filter((asset) => {
      if (asset.product?.family !== 'Networking' || !asset.address) return false;
      if (requestType === MOVE_REQUEST && productSelection) {
        return asset.product?.id === productSelection.id;
      }
      return true;
    });
  }, [locationInventory, productSelection, requestType]);

  const isFormValid = () =>
    isRequestFormValid({
      accountSelectionMissing,
      requestType,
      productSelection,
      // Only a Disconnect's validity reads the details, and watching them re-renders the form on every keystroke.
      details: requestType === DISCONNECT_REQUEST ? watch('details') : '',
      selectedDisconnectInventories,
      isP2PProduct,
      locationA,
      locationZ,
      newLocations,
      locationSelections,
      showAssetSelection,
      selectedAsset,
      showAdvancedLayout,
      schemaFormErrors,
      schemaFormData,
      requestSchema
    });

  const isShowWide = orderRequestSchema && showAdvancedLayout;
  const isCurrentTabSucceeded = requests[activeTab]?.status === 'success';
  return (
    <Dialog
      maxWidth={isShowWide ? 'md' : 'xs'}
      fullWidth
      open={open}
      onClose={disabled ? undefined : handleClose}>
      <div style={{ padding: '40px' }}>
        <TextBlock className="text-xl">Start a New Request</TextBlock>
        <TextSpan
          className="py-2 text-[13px] block mb-2"
          color="grey7"
          size="sm14">
          Submit a new request and your Account Manager will reach out to you shortly to learn more.
        </TextSpan>
        {requests.length > 1 && (
          <RequestTabs
            requests={requests}
            activeTab={activeTab}
            disabled={disabled}
            onChange={handleTabChange}
            onRemove={handleRemoveTab}
          />
        )}
        <form
          data-cy="service-request"
          aria-label="Service Request"
          onSubmit={handleSubmit(handleSubmitAll)}
          className={`flex flex-col items-start gap-6 ${isShowWide ? 'w-full max-w-6xl' : 'w-full'}`}>
          {/* Hidden submit button so pressing Enter in any field submits the form */}
          <button
            type="submit"
            tabIndex={-1}
            aria-hidden="true"
            style={{ display: 'none' }}
          />
          {showAccountSelector && (
            <AccountSelector
              disabled={disabled}
              options={accountOptions}
              value={selectedAccount}
              missing={accountSelectionMissing}
              onChange={handleAccountChange}
            />
          )}
          <div
            className={`w-full ${showAccountSelector ? '' : 'pt-5'} ${isShowWide ? 'columns-1 md:columns-2 gap-6' : ''}`}>
            {(showAdvancedLayout || showAccountSelector) && (
              <div
                className="text-gray-700"
                style={{ marginBottom: '0.5rem' }}>
                Request Details
              </div>
            )}
            <RequestTypeField
              disabled={disabled}
              registration={register('request_type', { onChange: requestTypeChanged })}
            />
            <ProductField
              disabled={disabled}
              options={productOptions}
              value={productSelection}
              onChange={productSelectionChanged}
              displayFamilyName={orderRequestPrefs.displayProductFamilyName}
            />
            {requestType === DISCONNECT_REQUEST && (
              <DisconnectFields
                disabled={disabled}
                options={filteredInventory}
                displayLocationCode={displayLocationCode}
                selected={selectedDisconnectInventories}
                onChange={setSelectedDisconnectInventories}
                detailsField={register('details')}
              />
            )}
            {showAssetSelection && requestType !== DISCONNECT_REQUEST && (
              <AssetSelectionField
                disabled={disabled}
                options={filteredInventory}
                displayLocationCode={displayLocationCode}
                requestType={requestType}
                selected={selectedAsset}
                onChange={setSelectedAsset}
              />
            )}
            {requestType !== CHANGE_REQUEST && requestType !== DISCONNECT_REQUEST && (
              <>
                <LocationFields
                  disabled={disabled}
                  requestType={requestType}
                  isP2PProduct={isP2PProduct}
                  options={filteredLocations}
                  displayLocationCode={displayLocationCode}
                  advancedNewLocationSettings={orderRequestPrefs.advancedNewLocationSettings}
                  activeTab={activeTab}
                  locationSelections={locationSelections}
                  onLocationSelectionsChange={setLocationSelections}
                  locationA={locationA}
                  locationZ={locationZ}
                  onLocationAChange={setLocationA}
                  onLocationZChange={setLocationZ}
                  newLocations={newLocations}
                  onNewLocationsChange={setNewLocations}
                />
                <PreferredTermField
                  disabled={disabled || requestType !== NEW_SERVICE_REQUEST}
                  value={preferredTermSelections}
                  onChange={setPreferredTermSelections}
                />
              </>
            )}
            {!showAdvancedLayout && requestType !== DISCONNECT_REQUEST && (
              <div style={{ breakInside: 'avoid', marginBottom: '1.5rem' }}>
                <TextField
                  label="Details for UPSTACK to Know"
                  id="details_textarea"
                  data-cy="details"
                  aria-label="details"
                  sx={{ width: '100%' }}
                  disabled={disabled}
                  minRows={3}
                  multiline
                  {...register('details')}></TextField>
              </div>
            )}
            {requestSchema && showAdvancedLayout && (
              <JSONSchemaForm
                formKey={`order-request-form-${requestSchema.id}-${productSelection?.id ?? ''}-${activeTab}`}
                schema={requestSchema.schema.validation_schema}
                uiSchema={dynamicUiSchema || requestSchema.schema.ui_schema}
                formData={schemaFormData}
                onChangeHandler={handleSchemaFormChange}
                onErrorHandler={setSchemaFormErrors}
                rjsfProps={{
                  widgets: widgets,
                  templates: { FieldTemplate: CustomFieldTemplate, ObjectFieldTemplate: CustomObjectFieldTemplate },
                  tagName: 'div',
                  formContext: { gap: requestType === CHANGE_REQUEST ? '4rem' : '1.5rem' }
                }}
              />
            )}
          </div>
          {attachmentsSupported && (
            <AttachmentsSection
              disabled={disabled}
              attachments={stagedAttachments}
              onAdd={attachmentActions.addStagedAttachments}
              onRemove={attachmentActions.removeStagedAttachment}
              onCancel={attachmentActions.cancelStagedUpload}
              onRetry={attachmentActions.requeueStagedAttachment}
            />
          )}
          <hr className="w-full" />
          <RequestFormActions
            disabled={disabled}
            valid={isFormValid()}
            offerAnotherRequest={!isCurrentTabSucceeded && requests.length < MAX_REQUESTS}
            anotherRequest={anotherRequest}
            onAnotherRequestChange={setAnotherRequest}
            onCancel={handleClose}
            onContinue={handleContinue}
            onSubmit={handleSubmitAll}
          />
        </form>
      </div>
    </Dialog>
  );
}
